import { useCallback, useMemo, useState } from "react";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import {
    useCreateReportTemplateMutation,
    useUpdateReportTemplateMutation,
} from "@api/reportTemplate.api";
import { useOverlays } from "contexts/OverlayContext";
import { COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP } from "../ChartOfAccountsTemplates/constants";
import {
    ARCHIVE_FILTER_ITEMS,
    ArchiveFilter,
} from "../templates/archiveFilter";
import {
    ApplyReportTemplateDrawer,
    ApplyReportTemplateDrawerTarget,
} from "./apply/ApplyReportTemplateDrawer";
import { ReportTemplateDetailsModal } from "./ReportTemplateDetailsModal";
import { KickReportTemplatePreviewModal } from "./preview/KickReportTemplatePreviewModal";
import {
    buildCreateReportTemplateRequest,
    buildUpdateReportTemplateRequest,
    emptyReportTemplateDetails,
    getDuplicateReportTemplateDetails,
    getEditReportTemplateDetails,
    ReportTemplateDetailsValues,
} from "./reportTemplateDetailsForm";
import {
    ReportTemplateCallbacks,
    ReportTemplatesList,
} from "./ReportTemplatesList";
import { defaultReportTemplatesView } from "./reportTemplatesView";
import {
    ReportTemplateRow,
    reportTemplatesViewConfig,
} from "./reportTemplatesViewConfig";
import { useReportTemplateLifecycle } from "./useReportTemplateLifecycle";
import { useReportTemplatesView } from "./useReportTemplatesView";

interface DetailsModalTarget {
    mode: "duplicate" | "edit";
    template: ReportTemplateRow;
}

export const ReportTemplatesPage = () => {
    const [search, setSearch] = useState("");
    const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const { toast } = useToaster();
    const [detailsTarget, setDetailsTarget] =
        useState<DetailsModalTarget | null>(null);
    const createTemplate = useCreateReportTemplateMutation();
    const updateTemplate = useUpdateReportTemplateMutation();
    const { archive, enable, setDefault, requestDelete, deleteModal } =
        useReportTemplateLifecycle();
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_REPORT_SETTINGS,
    );
    const { view, handleViewChange } = useReportTemplatesView();
    const { openOverlay } = useOverlays();
    const [drawerTarget, setDrawerTarget] =
        useState<ApplyReportTemplateDrawerTarget | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const openTemplateEditor = useCallback(
        (template: { templateId: string; templateName: string }) =>
            openOverlay("reportSettingsEditor", {
                kind: "template",
                ...template,
            }),
        [openOverlay],
    );

    const callbacks = useMemo<ReportTemplateCallbacks>(() => {
        const viewApplied = (template: ReportTemplateRow) =>
            setDrawerTarget({ template, entry: "viewApplied" });
        return {
            onApply: (template) =>
                setDrawerTarget({ template, entry: "apply" }),
            onViewApplied: viewApplied,
            onMenuItemSelect: ({ key, template }) => {
                const { templateId, name } = template;
                if (key === "preview") {
                    setIsPreviewOpen(true);
                    return;
                }
                if (key === "viewApplied") {
                    viewApplied(template);
                    return;
                }
                if (key === "duplicate") {
                    setDetailsTarget({ mode: "duplicate", template });
                    return;
                }
                if (key === "setDefault") {
                    void setDefault({ templateId, name });
                    return;
                }
                if (templateId === null) {
                    return;
                }
                switch (key) {
                    case "editDetails":
                        setDetailsTarget({ mode: "edit", template });
                        break;
                    case "editTemplate":
                        openTemplateEditor({ templateId, templateName: name });
                        break;
                    case "archive":
                        void archive({ templateId, name });
                        break;
                    case "enable":
                        void enable({ templateId, name });
                        break;
                    case "delete":
                        requestDelete({ templateId, name });
                        break;
                }
            },
        };
    }, [openTemplateEditor, archive, enable, setDefault, requestDelete]);

    const handleClearFilters = () => {
        setSearch("");
        setArchiveFilter("active");
    };

    const handleCreate = async ({
        values,
        sourceTemplateId,
    }: {
        values: ReportTemplateDetailsValues;
        sourceTemplateId: string | null;
    }) => {
        const created = await createTemplate.mutateAsync(
            buildCreateReportTemplateRequest({ values, sourceTemplateId }),
        );
        toast(`Created template "${created.name}"`);
        openTemplateEditor({
            templateId: created.id,
            templateName: created.name,
        });
    };

    const handleEdit = async ({
        templateId,
        values,
    }: {
        templateId: string;
        values: ReportTemplateDetailsValues;
    }) => {
        const updated = await updateTemplate.mutateAsync({
            templateId,
            body: buildUpdateReportTemplateRequest(values),
        });
        toast(`Updated template "${updated.name}"`);
    };

    const editTemplateId =
        detailsTarget?.mode === "edit"
            ? detailsTarget.template.templateId
            : null;

    return (
        <main>
            <TablePageHeader
                search={search}
                onSearchChange={setSearch}
                isFullPage
            >
                <HStack gap={8} className="ml-auto">
                    <Picker.Select
                        items={ARCHIVE_FILTER_ITEMS}
                        value={archiveFilter}
                        onValueChange={(value) =>
                            setArchiveFilter(value ?? "active")
                        }
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={reportTemplatesViewConfig}
                        defaultView={defaultReportTemplatesView}
                        onViewChange={handleViewChange}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyDisplayPosition={canManage ? "tooltip" : "none"}
                        hotKeyOptions={{
                            description: "New template",
                            preventDefault: true,
                        }}
                        tooltip={
                            canManage
                                ? undefined
                                : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP
                        }
                    >
                        <Button
                            variant="primary"
                            size="md"
                            disabled={!canManage}
                            onClick={() => setIsCreateModalOpen(true)}
                        >
                            New template
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>

            <ReportTemplatesList
                search={search}
                archiveFilter={archiveFilter}
                view={view}
                canManage={canManage}
                callbacks={callbacks}
                onViewChange={handleViewChange}
                onClearFilters={handleClearFilters}
            />

            <ApplyReportTemplateDrawer
                target={drawerTarget}
                onClose={() => setDrawerTarget(null)}
            />

            <KickReportTemplatePreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
            />

            <ReportTemplateDetailsModal
                show={isCreateModalOpen}
                title="Create template"
                submitLabel="Create"
                initialValues={emptyReportTemplateDetails}
                onSubmit={(values) =>
                    handleCreate({ values, sourceTemplateId: null })
                }
                onHide={() => setIsCreateModalOpen(false)}
            />

            {detailsTarget?.mode === "duplicate" && (
                <ReportTemplateDetailsModal
                    show
                    title="Create template"
                    submitLabel="Create"
                    initialValues={getDuplicateReportTemplateDetails(
                        detailsTarget.template,
                    )}
                    onSubmit={(values) =>
                        handleCreate({
                            values,
                            sourceTemplateId: detailsTarget.template.templateId,
                        })
                    }
                    onHide={() => setDetailsTarget(null)}
                />
            )}

            {detailsTarget?.mode === "edit" && editTemplateId !== null && (
                <ReportTemplateDetailsModal
                    show
                    title="Edit report template"
                    submitLabel="Save changes"
                    initialValues={getEditReportTemplateDetails(
                        detailsTarget.template,
                    )}
                    onSubmit={(values) =>
                        handleEdit({ templateId: editTemplateId, values })
                    }
                    onHide={() => setDetailsTarget(null)}
                />
            )}

            {deleteModal}
        </main>
    );
};
